// ─────────────────────────────────────────────────────────────────────────
// charts.ts — hand-rolled inline SVG (no chart library). Returns SVG strings;
// fills reference the --sc-* CSS custom properties so charts inherit the theme.
// ─────────────────────────────────────────────────────────────────────────

import type { LifetimeYear } from '../core/types.js';

const money = (n: number): string =>
  '$' + Math.round(n).toLocaleString('en-AU');

/** Bill before vs after: two vertical bars. */
export function billChart(before: number, after: number): string {
  const max = Math.max(before, after, 1);
  const h = 120;
  const barH = (v: number): number => Math.max(2, (v / max) * h);
  const bBefore = barH(before);
  const bAfter = barH(after);
  return `
<svg viewBox="0 0 220 170" role="img" aria-label="Annual bill before ${money(before)} and after ${money(after)}">
  <g font-family="var(--_font-mono)" font-size="12">
    <rect x="30" y="${20 + (h - bBefore)}" width="60" height="${bBefore}" rx="3" fill="var(--_muted)"/>
    <rect x="130" y="${20 + (h - bAfter)}" width="60" height="${bAfter}" rx="3" fill="var(--_positive)"/>
    <text x="60" y="${14 + (h - bBefore)}" text-anchor="middle" fill="var(--_ink)">${money(before)}</text>
    <text x="160" y="${14 + (h - bAfter)}" text-anchor="middle" fill="var(--_positive)">${money(after)}</text>
    <text x="60" y="158" text-anchor="middle" fill="var(--_muted)" font-family="var(--_font)">Now</text>
    <text x="160" y="158" text-anchor="middle" fill="var(--_muted)" font-family="var(--_font)">With solar</text>
  </g>
</svg>`.trim();
}

/** 25-year cumulative net: a line from −cost rising through the break-even axis.
 *  The marker is placed at the EXACT payback (fractional year) and labelled with
 *  the same number shown on the payback card — no "yr 8 vs 7.4" mismatch. */
export function cumulativeChart(
  lifetime: LifetimeYear[],
  paybackYears: number | null,
): string {
  if (lifetime.length === 0) return '';
  const w = 260;
  const h = 150;
  const pad = 4;
  const xs = lifetime.map((d) => d.year);
  const ys = lifetime.map((d) => d.cumulativeNet);
  const minY = Math.min(0, ...ys);
  const maxY = Math.max(0, ...ys);
  const spanY = maxY - minY || 1;
  const maxX = Math.max(...xs);
  const px = (x: number): number => pad + ((x - 1) / (maxX - 1 || 1)) * (w - 2 * pad);
  const py = (y: number): number => pad + (1 - (y - minY) / spanY) * (h - 2 * pad);
  const zeroY = py(0);
  const path = lifetime.map((d, i) => `${i === 0 ? 'M' : 'L'}${px(d.year).toFixed(1)},${py(d.cumulativeNet).toFixed(1)}`).join(' ');
  // Marker at the exact payback year (matches the payback card's figure).
  const marker =
    paybackYears != null && paybackYears <= maxX
      ? `<circle cx="${px(paybackYears).toFixed(1)}" cy="${zeroY.toFixed(1)}" r="3.5" fill="var(--_accent)"/>
       <text x="${px(paybackYears).toFixed(1)}" y="${(zeroY - 7).toFixed(1)}" text-anchor="middle" font-size="10" font-family="var(--_font-mono)" fill="var(--_accent-ink)">${paybackYears} yr</text>`
      : '';
  return `
<svg viewBox="0 0 ${w} ${h}" role="img" aria-label="Cumulative net position over 25 years">
  <line x1="${pad}" y1="${zeroY.toFixed(1)}" x2="${w - pad}" y2="${zeroY.toFixed(1)}" stroke="var(--_edge)" stroke-dasharray="3 3"/>
  <path d="${path}" fill="none" stroke="var(--_accent)" stroke-width="2.5" stroke-linejoin="round"/>
  ${marker}
</svg>`.trim();
}

/** Where your solar goes: a donut ring, self-consumed vs exported. */
export function splitChart(selfKwh: number, exportKwh: number): string {
  const total = selfKwh + exportKwh || 1;
  const selfPct = (selfKwh / total) * 100;
  const exportPct = 100 - selfPct;
  const r = 54;
  const c = 2 * Math.PI * r; // circumference
  const selfLen = (selfPct / 100) * c;
  return `
<svg viewBox="0 0 220 150" role="img" aria-label="${Math.round(selfPct)}% self-consumed, ${Math.round(exportPct)}% exported">
  <g transform="translate(75 75)">
    <circle r="${r}" fill="none" stroke="var(--_edge)" stroke-width="14"/>
    <circle r="${r}" fill="none" stroke="var(--_accent)" stroke-width="14" opacity="0.55"
      stroke-dasharray="${(c - selfLen).toFixed(1)} ${selfLen.toFixed(1)}"
      stroke-dashoffset="${(-selfLen).toFixed(1)}" transform="rotate(-90)"/>
    <circle r="${r}" fill="none" stroke="var(--_positive)" stroke-width="14"
      stroke-dasharray="${selfLen.toFixed(1)} ${(c - selfLen).toFixed(1)}" transform="rotate(-90)"/>
    <text text-anchor="middle" y="-2" font-family="var(--_font-mono)" font-weight="700" font-size="22" fill="var(--_ink)">${Math.round(selfPct)}%</text>
    <text text-anchor="middle" y="16" font-family="var(--_font)" font-size="10" fill="var(--_muted)">used at home</text>
  </g>
  <g font-size="11" font-family="var(--_font)">
    <rect x="150" y="52" width="10" height="10" rx="2" fill="var(--_positive)"/>
    <text x="164" y="61" fill="var(--_ink-soft)">Used ${Math.round(selfPct)}%</text>
    <rect x="150" y="74" width="10" height="10" rx="2" fill="var(--_accent)" opacity="0.55"/>
    <text x="164" y="83" fill="var(--_ink-soft)">Export ${Math.round(exportPct)}%</text>
  </g>
</svg>`.trim();
}
